import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormButton } from '@app/core/components/form-field-button/form-field-button.component';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { GoogleTranslateService } from '@app/services/google-translate.service';
import { TranslationEditGet, TranslationEditItem, TranslationLanguage, TranslationListService } from '@app/services/translation-list.service';

@Component({
    selector: 'tt-translate-edit-page',
    templateUrl: './translate-edit.component.html',
    styleUrls: ['./translate-edit.component.css'],
    standalone: false,
})
export class TranslateEditPageComponent implements OnInit {
    public page?: TranslationEditGet;
    public translationEdits: TranslationEditRow[] = [];
    public translateFromLanguages: TranslationLanguage[] = [];
    public translateAll: '0' | '1' = '1';
    public translating = false;
    public saving = false;
    private itemId = '';

    public readonly translateFromLanguageButtons: FormButton[] = [{ id: 'erase', icon: 'fas fa-eraser', type: 'danger', onClick: () => this.clearTranslateFromLanguage() }];

    constructor(
        private translationList: TranslationListService,
        private googleTranslate: GoogleTranslateService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async ngOnInit(): Promise<void> {
        this.itemId = `${this.navigator?.params?.['item_id'] ?? ''}`;
        if (this.itemId) await this.init();
    }

    public clearTranslateFromLanguage(): void {
        if (this.page) this.page.translateedit_translate_from_language = '';
    }

    public onTranslateAllChanged(value?: '0' | '1'): void {
        if (value === '1' || value === '0') {
            this.translationEdits.forEach((translation) => (translation.toTranslate = value));
            this.translateAll = value;
            return;
        }

        this.translateAll = this.translationEdits.every((translation) => translation.toTranslate === '1') ? '1' : '0';
    }

    public onToTranslateChanged(value: '0' | '1', translation: TranslationEditRow): void {
        translation.toTranslate = value;
        this.onTranslateAllChanged();
    }

    public async translate(): Promise<void> {
        if (!this.page || this.translating) return;
        this.translating = true;
        try {
            const sourceLanguage = await this.getSourceLanguage();
            if (!sourceLanguage) return;

            for (const translation of this.translationEdits) {
                if (translation.toTranslate !== '1') continue;

                if (translation.google_language_id === 'nn' && this.isNordicLanguage(sourceLanguage)) {
                    translation.wordlang_name = await this.googleTranslate.translateToNorwegianNynorsk(this.page.translateedit_text_to_translate, sourceLanguage);
                } else if (translation.google_language_id === sourceLanguage) {
                    translation.wordlang_name = this.page.translateedit_text_to_translate;
                } else if (!['sy', 'nn'].includes(translation.language_id)) {
                    translation.wordlang_name = (await this.googleTranslate.translateText(this.page.translateedit_text_to_translate, translation.google_language_id, sourceLanguage))[0]?.translatedText ?? '';
                }
            }
        } finally {
            this.translating = false;
        }
    }

    public async saveTranslations(): Promise<void> {
        if (this.saving) return;
        this.saving = true;
        try {
            await this.translationList.saveTranslationEdits(this.translationEdits);
            this.navigator?.back();
        } finally {
            this.saving = false;
        }
    }

    public trackByTranslation(_: number, translation: TranslationEditRow): string {
        return `${translation.word_id}-${translation.language_id}`;
    }

    private async init(): Promise<void> {
        const [page, translations, languages] = await Promise.all([
            this.translationList.getTranslationEdit(this.itemId),
            this.translationList.getTranslationEdits(this.itemId),
            this.translationList.getLanguages('1'),
        ]);
        this.page = page;
        this.translationEdits = translations.map((translation) => ({ ...translation, toTranslate: '1' }));
        this.translateFromLanguages = languages;
    }

    private async getSourceLanguage(): Promise<string | null> {
        if (!this.page) return null;
        if (this.page.translateedit_translate_from_language) return this.page.translateedit_translate_from_language.toLowerCase();
        return this.googleTranslate.detectLanguage(this.page.translateedit_text_to_translate.toLowerCase());
    }

    private isNordicLanguage(language: string): language is 'da' | 'no' | 'sv' {
        return language === 'da' || language === 'no' || language === 'sv';
    }

}

interface TranslationEditRow extends TranslationEditItem {
    toTranslate: '0' | '1';
}
